import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Search, Lock, ArrowLeft, Users, FileText } from "lucide-react";
import { API, LOGO_URL, STUDIO } from "@/constants/content";
import ContentEditor from "@/components/admin/ContentEditor";

const TOKEN_KEY = "tlh_admin_token";

const MAGNET_LABELS = {
  sport: "Sportolók",
  suli: "Szülők / Suli",
  aroma: "Aroma Flow",
  general: "Mozgás Éjszakája",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [magnetFilter, setMagnetFilter] = useState("all");
  const [topTab, setTopTab] = useState("subscribers"); // 'subscribers' | 'content'

  const load = useCallback(async (tk) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/subscribers`, {
        headers: { "X-Admin-Token": tk },
      });
      setSubs(data.subscribers || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("Érvénytelen jelszó. Próbáld újra.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError("Hiba történt a betöltés közben.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    localStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSubs([]);
  };

  const downloadCsv = () => {
    const url = `${API}/admin/subscribers.csv?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
    toast.success("CSV letöltés elindítva");
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-[2rem] border border-[#EAE5D9] shadow-xl p-10 paper-grain relative overflow-hidden">
          <div className="flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="" className="h-12 w-12 rounded-full object-cover ring-1 ring-[#D4AF37]/40" />
            <div>
              <div className="font-serif-display text-[#1A2825] text-xl">Admin felület</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#A88A2B]">{STUDIO.name}</div>
            </div>
          </div>
          <h1 className="font-serif-display text-2xl text-[#1A2825]">Lépj be a feliratkozóid kezeléséhez.</h1>
          <p className="mt-2 text-sm text-[#3A4D4A] font-light leading-relaxed">
            Add meg az admin jelszót, amit az oldal kezelője adott át Neked.
          </p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Admin jelszó</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#758C88]" />
                <input
                  type="password"
                  required
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  data-testid="admin-token-input"
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-3 bg-white border border-[#EAE5D9] focus:border-[#2A9D8F] focus:ring-1 focus:ring-[#2A9D8F] outline-none rounded-2xl text-[#1A2825]"
                />
              </div>
            </div>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              type="submit"
              data-testid="admin-login-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#1C6B61] text-white px-6 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.32)]"
            >
              Belépek
            </button>
            <Link to="/" className="block text-center text-sm text-[#3A4D4A] hover:text-[#2A9D8F]">
              <ArrowLeft className="inline h-3 w-3 mr-1" /> Vissza a főoldalra
            </Link>
          </form>
        </div>
      </div>
    );
  }

  // ---- Filter ----
  const filtered = subs.filter((s) => {
    const matchMagnet = magnetFilter === "all" ? true : s.magnet === magnetFilter;
    const q = query.trim().toLowerCase();
    const matchQ = !q || (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q);
    return matchMagnet && matchQ;
  });

  const counts = subs.reduce((acc, s) => {
    const k = s.magnet || "general";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A2825]">
      <header className="bg-white border-b border-[#EAE5D9] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-9 w-9 rounded-full object-cover ring-1 ring-[#D4AF37]/40" />
            <div className="leading-tight">
              <div className="font-serif-display text-base md:text-lg">Admin · Feliratkozók</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#A88A2B]">{STUDIO.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#3A4D4A] hover:text-[#2A9D8F]">
              <ArrowLeft className="h-3.5 w-3.5" /> Oldal
            </Link>
            <button
              type="button"
              onClick={() => load(token)}
              className="inline-flex items-center gap-1.5 bg-white border border-[#EAE5D9] text-[#3A4D4A] hover:border-[#2A9D8F] px-3 py-2 rounded-full text-sm"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Frissítés
            </button>
            <button
              type="button"
              data-testid="admin-csv-download"
              onClick={downloadCsv}
              className="inline-flex items-center gap-1.5 bg-[#2A9D8F] hover:bg-[#1C6B61] text-white px-3 py-2 rounded-full text-sm"
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 bg-white border border-[#EAE5D9] text-[#3A4D4A] hover:border-[#A88A2B] px-3 py-2 rounded-full text-sm"
            >
              <LogOut className="h-3.5 w-3.5" /> Kilépés
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        {/* Top tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#EAE5D9]">
          <button
            type="button"
            onClick={() => setTopTab("subscribers")}
            data-testid="admin-tab-subscribers"
            className={`relative px-5 py-3 text-sm font-medium tracking-wide transition-colors ${
              topTab === "subscribers" ? "text-[#1C6B61]" : "text-[#758C88] hover:text-[#3A4D4A]"
            }`}
          >
            <span className="inline-flex items-center gap-2">
              <Users className="h-4 w-4" /> Feliratkozók
            </span>
            {topTab === "subscribers" && <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#D4AF37]" />}
          </button>
          <button
            type="button"
            onClick={() => setTopTab("content")}
            data-testid="admin-tab-content"
            className={`relative px-5 py-3 text-sm font-medium tracking-wide transition-colors ${
              topTab === "content" ? "text-[#1C6B61]" : "text-[#758C88] hover:text-[#3A4D4A]"
            }`}
          >
            <span className="inline-flex items-center gap-2">
              <FileText className="h-4 w-4" /> Tartalom szerkesztése
            </span>
            {topTab === "content" && <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#D4AF37]" />}
          </button>
        </div>

        {topTab === "content" ? (
          <ContentEditor token={token} />
        ) : (
          <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-8">
          <StatCard label="Összes" count={subs.length} accent="#1C6B61" />
          {Object.entries(MAGNET_LABELS).map(([k, label]) => (
            <StatCard key={k} label={label} count={counts[k] || 0} accent="#2A9D8F" />
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#758C88]" />
            <input
              type="text"
              data-testid="admin-search-input"
              placeholder="Keresés név vagy email szerint…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#EAE5D9] focus:border-[#2A9D8F] focus:ring-1 focus:ring-[#2A9D8F] outline-none rounded-full text-sm"
            />
          </div>
          <select
            data-testid="admin-magnet-filter"
            value={magnetFilter}
            onChange={(e) => setMagnetFilter(e.target.value)}
            className="bg-white border border-[#EAE5D9] rounded-full px-4 py-2.5 text-sm focus:border-[#2A9D8F] focus:ring-1 focus:ring-[#2A9D8F] outline-none"
          >
            <option value="all">Minden lista</option>
            {Object.entries(MAGNET_LABELS).map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#EAE5D9] rounded-2xl overflow-hidden">
          {loading && !subs.length ? (
            <div className="p-10 text-center text-[#758C88]">Betöltés…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-8 w-8 text-[#D4AF37] mx-auto mb-3" />
              <div className="font-medium">Nincs még feliratkozó ezzel a szűrővel.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F3EB] text-[#A88A2B] text-[10px] uppercase tracking-[0.25em]">
                  <tr>
                    <th className="text-left py-3 px-4">Név</th>
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Magnet</th>
                    <th className="text-left py-3 px-4">Forrás</th>
                    <th className="text-left py-3 px-4">Dátum</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s, i) => (
                    <tr
                      key={s.id || `${s.email}-${i}`}
                      data-testid={`admin-subscriber-row-${i}`}
                      className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]"
                    >
                      <td className="py-3 px-4 font-medium">{s.name}</td>
                      <td className="py-3 px-4 text-[#3A4D4A] break-all">{s.email}</td>
                      <td className="py-3 px-4">
                        <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#2A9D8F]/10 text-[#1C6B61] border border-[#2A9D8F]/30">
                          {MAGNET_LABELS[s.magnet] || s.magnet || "—"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-[#758C88]">{s.source || "—"}</td>
                      <td className="py-3 px-4 text-[#758C88]">
                        {s.created_at ? new Date(s.created_at).toLocaleString("hu-HU") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-[#758C88]">
          A feliratkozók a Brevo listáidba is bekerülnek (sport / suli / aroma / általános).
        </div>
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, count, accent }) {
  return (
    <div className="bg-white border border-[#EAE5D9] rounded-2xl px-4 py-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">{label}</div>
      <div className="font-serif-display text-3xl mt-1" style={{ color: accent }}>{count}</div>
    </div>
  );
}
